import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, LinearProgress, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, Snackbar, Alert, InputAdornment, Stack
} from '@mui/material';
import {
  Folder as FolderIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import InvoicesLegacy from './InvoicesLegacy';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import CheckCapture from '../components/CheckCapture';

const NAVY = '#1a237e';

const statusColors = {
  partial: { bg: '#fff3e0', text: '#e65100' },
  paid:      { bg: '#e8f5e9', text: '#2e7d32', label: 'Paid' },
  approved:  { bg: '#e3f2fd', text: '#1565c0', label: 'Approved' },
  pending:   { bg: '#f5f5f5', text: '#616161', label: 'Pending Approval' },
  due_today: { bg: '#fff8e1', text: '#f57c00', label: 'Due today' },
  overdue:   { bg: '#ffebee', text: '#c62828', label: 'Overdue' },
};

const money = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyExact = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};

function InvoicesNew() {
  const { user } = useAuth();

  // State
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [summary, setSummary] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ vendors: [], contracts: [] });
  const [vendorId, setVendorId] = useState('');
  const [contractId, setContractId] = useState('');
  const [statusTab, setStatusTab] = useState('outstanding');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Bulk operation dialogs
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidForm, setMarkPaidForm] = useState({ payment_date: new Date().toISOString().slice(0,10), payment_method: 'check', payment_reference: '' });
  const [bulkBusy, setBulkBusy] = useState(false);

  // === Per-invoice action dialogs (approve / mark-paid / payment-detail) ===
  const [actionDialog, setActionDialog] = useState({ type: null, invoice: null });
  const [proofForm, setProofForm] = useState({ files: [], notify: true, busy: false });
  const [singlePayForm, setSinglePayForm] = useState({
    payment_date: new Date().toISOString().slice(0,10),
    payment_method: 'check',
    payment_reference: '',
    attachments: [],
    cc_gc: false,
    amount: '',
  });
  const [payHistory, setPayHistory] = useState([]);
  const [checkCaptureOpen, setCheckCaptureOpen] = useState(false);
  useEffect(() => {
    if (actionDialog.type === 'paymentDetail' && actionDialog.invoice) {
      api.get(`/invoices/${actionDialog.invoice.id}/payments`)
        .then(r => setPayHistory(Array.isArray(r.data) ? r.data : []))
        .catch(() => setPayHistory([]));
    } else {
      setPayHistory([]);
    }
  }, [actionDialog.type, actionDialog.invoice]);
  const [actionBusy, setActionBusy] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: null, title: '' });

  // Detail dialog
  const [detail, setDetail] = useState(null);

  // === Load projects + last selected ===
  useEffect(() => {
    (async () => {
      try {
        const projRes = await api.get('/projects');
        setProjects(projRes.data || []);
        const lastRes = await api.get('/invoices/last-project').catch(() => ({ data: { project_id: null } }));
        const lastId = lastRes.data.project_id;
        if (lastId && projRes.data.find(p => p.id === lastId)) {
          setProjectId(lastId);
        }
      } catch (e) {
        showAlert('Failed to load projects', 'error');
      }
    })();
  }, []);

  // === Persist project + reload summary/filter-options/rows ===
  useEffect(() => {
    if (!projectId) {
      setSummary(null);
      setFilterOptions({ vendors: [], contracts: [] });
      setRows([]);
      return;
    }
    api.put('/invoices/last-project', { project_id: projectId }).catch(() => {});
    loadSummary();
    loadFilterOptions();
    setVendorId('');
    setContractId('');
    setStatusTab('outstanding');
    setSelectedIds(new Set());
  }, [projectId]);

  // === Reload rows when filters change ===
  useEffect(() => {
    if (projectId) loadRows();
  }, [projectId, vendorId, contractId, statusTab]);

  async function loadSummary() {
    try {
      const r = await api.get('/invoices/project-summary', { params: { project_id: projectId } });
      setSummary(r.data);
    } catch (e) { console.error(e); }
  }
  async function loadFilterOptions() {
    try {
      const r = await api.get('/invoices/filter-options', { params: { project_id: projectId } });
      setFilterOptions(r.data);
    } catch (e) { console.error(e); }
  }
  async function loadRows() {
    setLoading(true);
    try {
      const params = { project_id: projectId };
      if (vendorId) params.vendor_id = vendorId;
      if (contractId) params.contract_id = contractId;
      if (statusTab !== 'all') params.status = statusTab;
      const r = await api.get('/invoices/list', { params });
      setRows(r.data || []);
    } catch (e) {
      console.error(e);
      showAlert('Failed to load invoices', 'error');
    } finally {
      setLoading(false);
    }
  }

  const showAlert = (message, severity = 'success') => setAlert({ show: true, message, severity });

  async function handleReplaceProof() {
    if (!actionDialog.invoice) return;
    if (!proofForm.files || proofForm.files.length === 0) { showAlert('Upload a check image first', 'error'); return; }
    setProofForm(f => ({ ...f, busy: true }));
    try {
      await api.put(`/invoices/${actionDialog.invoice.id}/payment-proof`, {
        attachments: proofForm.files,
        notify: proofForm.notify,
      });
      showAlert(proofForm.notify ? 'Payment proof updated — corrected email sent' : 'Payment proof updated');
      setActionDialog({ type: null, invoice: null });
      setProofForm({ files: [], notify: true, busy: false });
      loadRows();
    } catch (e) {
      setProofForm(f => ({ ...f, busy: false }));
      showAlert(e.response?.data?.error || 'Failed to update payment proof', 'error');
    }
  }

  // === Per-invoice action handlers ===
  async function handleApprove() {
    if (!actionDialog.invoice) return;
    setActionBusy(true);
    try {
      await api.put(`/invoices/${actionDialog.invoice.id}/approve`);
      showAlert('Invoice approved');
      setActionDialog({ type: null, invoice: null });
      await Promise.all([loadRows(), loadSummary()]);
    } catch (e) {
      console.error(e);
      showAlert(e.response?.data?.error || 'Failed to approve invoice', 'error');
    } finally { setActionBusy(false); }
  }

  async function handleSingleMarkPaid() {
    if (!actionDialog.invoice) return;
    if (!singlePayForm.payment_date) { showAlert('Payment date is required', 'error'); return; }
    const amt = parseFloat(singlePayForm.amount);
    if (!amt || isNaN(amt) || amt <= 0) { showAlert('Enter a payment amount', 'error'); return; }
    setActionBusy(true);
    try {
      const res = await api.post(`/invoices/${actionDialog.invoice.id}/payments`, singlePayForm);
      showAlert(res.data?.status === 'paid' ? 'Payment recorded — invoice paid in full' : 'Partial payment recorded');
      setActionDialog({ type: null, invoice: null });
      await Promise.all([loadRows(), loadSummary()]);
    } catch (e) {
      console.error(e);
      showAlert(e.response?.data?.error || 'Failed to record payment', 'error');
    } finally { setActionBusy(false); }
  }

  // === Filtered display rows (search filter is client-side) ===
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.invoice_number || '').toLowerCase().includes(q) ||
      (r.notes || '').toLowerCase().includes(q) ||
      (r.vendor_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  // === Bulk selection ===
  const allSelected = filteredRows.length > 0 && filteredRows.every(r => selectedIds.has(r.id));
  const someSelected = filteredRows.some(r => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    const newSet = new Set(selectedIds);
    if (allSelected) filteredRows.forEach(r => newSet.delete(r.id));
    else filteredRows.forEach(r => newSet.add(r.id));
    setSelectedIds(newSet);
  };
  const toggleRow = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };
  const selectedTotal = useMemo(
    () => filteredRows.filter(r => selectedIds.has(r.id)).reduce((s, r) => s + Number(r.amount), 0),
    [filteredRows, selectedIds]
  );

  // === Bulk actions ===
  const doMarkPaid = async () => {
    setBulkBusy(true);
    try {
      await api.put('/invoices/bulk-mark-paid', {
        invoice_ids: Array.from(selectedIds),
        ...markPaidForm,
      });
      showAlert(`${selectedIds.size} invoice(s) marked as paid`);
      setMarkPaidOpen(false);
      setSelectedIds(new Set());
      loadRows();
      loadSummary();
    } catch (e) {
      showAlert(e.response?.data?.error || 'Failed to mark paid', 'error');
    } finally { setBulkBusy(false); }
  };

  const doSendReminders = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post('/invoices/bulk-send-reminders', {
        invoice_ids: Array.from(selectedIds),
      });
      const { sent = 0, skipped = 0, errors = 0 } = r.data || {};
      showAlert(`Sent ${sent}, skipped ${skipped}, errors ${errors}`);
      setSelectedIds(new Set());
    } catch (e) {
      showAlert(e.response?.data?.error || 'Failed to send reminders', 'error');
    } finally { setBulkBusy(false); }
  };

  const doExport = () => {
    const ids = Array.from(selectedIds).join(',');
    const url = `${api.defaults.baseURL}/invoices/bulk-export?invoice_ids=${ids}`;
    // Use a temporary anchor with the auth token
    api.get('/invoices/bulk-export', {
      params: { invoice_ids: ids },
      responseType: 'blob'
    }).then(r => {
      const blob = new Blob([r.data], { type: 'text/csv' });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `invoices-${projectId.slice(0,8)}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(dlUrl);
    }).catch(e => showAlert('Export failed', 'error'));
  };

  const selectedProject = projects.find(p => p.id === projectId);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 0.5 }}>Invoices</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Track payments across all your projects</Typography>

      {/* === Project picker (mandatory) === */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Select a project</InputLabel>
          <Select value={projectId} label="Select a project" onChange={e => setProjectId(e.target.value)}>
            {projects.length === 0 && <MenuItem disabled>No projects available</MenuItem>}
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{p.name}</Typography>
                  {p.address && <Typography variant="caption" sx={{ color: '#888' }}>{p.address}</Typography>}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* === Empty state === */}
      {!projectId && (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <FolderIcon sx={{ fontSize: 64, color: '#ddd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#888', mb: 1 }}>Select a project to view its invoices</Typography>
          <Typography variant="body2" sx={{ color: '#aaa' }}>Pick a project from the dropdown above to see invoice details, status, and totals.</Typography>
        </Paper>
      )}

      {/* === Project summary card === */}
      {projectId && summary && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, background: 'linear-gradient(135deg, #f5f7ff 0%, #ffffff 100%)' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <FolderIcon sx={{ color: NAVY }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>{summary.project.name}</Typography>
            {summary.project.address && <Typography variant="body2" sx={{ color: '#888' }}>·  {summary.project.address}</Typography>}
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <Typography variant="caption" sx={{ color: '#888' }}>Contracted</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>{money(summary.contracted)}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{summary.contract_count} contract{summary.contract_count !== 1 ? 's' : ''}</Typography>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Typography variant="caption" sx={{ color: '#888' }}>Invoiced</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>{money(summary.invoiced)}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{summary.invoice_count} invoice{summary.invoice_count !== 1 ? 's' : ''}</Typography>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Typography variant="caption" sx={{ color: '#888' }}>Paid</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>{money(summary.paid)}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{summary.invoiced > 0 ? Math.round(100 * summary.paid / summary.invoiced) : 0}% of invoiced</Typography>
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Typography variant="caption" sx={{ color: '#888' }}>Outstanding</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: summary.outstanding > 0 ? '#f57c00' : '#999' }}>{money(summary.outstanding)}</Typography>
              {summary.overdue_count > 0 ? (
                <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>{summary.overdue_count} overdue</Typography>
              ) : (
                <Typography variant="caption" sx={{ color: '#aaa' }}>None overdue</Typography>
              )}
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Typography variant="caption" sx={{ color: '#888' }}>Remaining on Contracts</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>{money(summary.remaining)}</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>{summary.contracted > 0 ? Math.round(100 * summary.paid / summary.contracted) : 0}% complete</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* === Filters === */}
      {projectId && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select value={vendorId} label="Vendor" onChange={e => { setVendorId(e.target.value); setContractId(''); }}>
                  <MenuItem value="">All vendors</MenuItem>
                  {filterOptions.vendors.map(v => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Contract</InputLabel>
                <Select value={contractId} label="Contract" onChange={e => setContractId(e.target.value)}>
                  <MenuItem value="">All contracts</MenuItem>
                  {filterOptions.contracts
                    .filter(c => !vendorId || rows.some(r => r.contract_id === c.id && (r.vendor_id === vendorId)))
                    .map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.label} — {money(c.amount)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small" placeholder="Search invoices, descriptions, vendors..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa' }} /></InputAdornment> }}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {[
              { key: 'all', label: 'All' },
              { key: 'outstanding', label: 'Outstanding' },
              { key: 'overdue', label: 'Overdue' },
              { key: 'paid', label: 'Paid' },
            ].map(tab => (
              <Chip
                key={tab.key} label={tab.label}
                onClick={() => setStatusTab(tab.key)}
                sx={{
                  bgcolor: statusTab === tab.key ? NAVY : '#f0f0f0',
                  color: statusTab === tab.key ? 'white' : '#666',
                  fontWeight: 600, cursor: 'pointer',
                  '&:hover': { bgcolor: statusTab === tab.key ? NAVY : '#e0e0e0' }
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* === Bulk action bar === */}
      {projectId && selectedIds.size > 0 && (
        <Paper sx={{ p: 1.5, mb: 1, borderRadius: 2, bgcolor: '#f8f9ff', border: '1px solid #c5cae9', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontWeight: 600, color: NAVY }}>
            {selectedIds.size} selected · Total {moneyExact(selectedTotal)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" disabled={bulkBusy} onClick={doExport} startIcon={<DownloadIcon />}>Export CSV</Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </Paper>
      )}

      {/* === Invoice table === */}
      {projectId && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell padding="checkbox">
                  <Checkbox indeterminate={someSelected && !allSelected} checked={allSelected} onChange={toggleSelectAll} />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contract</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Due</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>View</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: '#999' }}>No invoices match the current filters.</TableCell></TableRow>
              )}
              {filteredRows.map(r => {
                const isSel = selectedIds.has(r.id);
                const sCfg = statusColors[r.aging_status] || statusColors.pending;
                const invoiceUrl = r.invoice_document_url || (Array.isArray(r.invoice_attachments) ? r.invoice_attachments?.[0]?.url : null);
                const payUrl = r.payment_proof_url || (Array.isArray(r.attachments) ? r.attachments?.[0]?.url : null);
                return (
                  <TableRow key={r.id} hover sx={{ bgcolor: isSel ? '#f8f9ff' : 'inherit' }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSel} onChange={() => toggleRow(r.id)} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: NAVY }}>{r.invoice_number}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip title={r.notes || ''}>
                        <Typography variant="body2" noWrap sx={{ color: '#555' }}>{r.notes || '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{r.vendor_name || '—'}</TableCell>
                    <TableCell>
                      {r.contract_id ? (
                        <Tooltip title={[r.contract_title, r.contract_amount ? moneyExact(r.contract_amount) : null, r.contract_trade].filter(Boolean).join(' · ') || ''}>
                          <Chip size="small" label={r.contract_number || r.contract_title || r.contract_id.slice(0,8)} sx={{ fontSize: '0.72rem', maxWidth: 180 }} />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>{moneyExact(r.amount)}</TableCell>
                    <TableCell>{formatDate(r.invoice_date)}</TableCell>
                    <TableCell sx={{ color: r.aging_status === 'overdue' ? '#c62828' : 'inherit' }}>{formatDate(r.due_date)}</TableCell>
                    <TableCell>
                      {(() => {
                        const st = r.status;
                        const isOverdue = !['paid','closed'].includes(st) && r.days_overdue > 0;
                        const canAct = ['homeowner','general_contractor','admin'].includes(user?.role);
                        if (st === 'submitted' || st === 'under_review') {
                          const c = isOverdue ? statusColors.overdue : statusColors.pending;
                          const lbl = isOverdue ? `Pending · ${r.days_overdue}d overdue` : 'Pending Approval';
                          return <Chip size="small" label={lbl}
                            onClick={canAct ? () => setActionDialog({ type: 'approve', invoice: r }) : undefined}
                            sx={{ bgcolor: c.bg, color: c.text, fontWeight: 600, cursor: canAct ? 'pointer' : 'default' }} />;
                        }
                        if (st === 'partially_paid') {
                          const paid = Number(r.amount_paid || 0);
                          const total = Number(r.amount);
                          const cc = statusColors.partial || { bg: '#fff3e0', text: '#e65100' };
                          return <Chip size="small" label={`Partial · ${moneyExact(paid)}/${moneyExact(total)}`}
                            onClick={canAct ? () => {
                              setSinglePayForm({ payment_date: new Date().toISOString().slice(0,10), payment_method: 'check', payment_reference: '', attachments: [], cc_gc: !!r.visible_to_gc, amount: String(Math.max(0, total - paid)) });
                              setActionDialog({ type: 'markPaid', invoice: r });
                            } : undefined}
                            sx={{ bgcolor: cc.bg, color: cc.text, fontWeight: 600, cursor: canAct ? 'pointer' : 'default' }} />;
                        }
                        if (st === 'approved') {
                          const c = isOverdue ? statusColors.overdue : statusColors.approved;
                          const lbl = isOverdue ? `Approved · ${r.days_overdue}d overdue` : 'Approved';
                          return <Chip size="small" label={lbl}
                            onClick={canAct ? () => {
                              setSinglePayForm({ payment_date: new Date().toISOString().slice(0,10), payment_method: 'check', payment_reference: '', attachments: [], cc_gc: !!r.visible_to_gc, amount: String(Math.max(0, Number(r.amount) - Number(r.amount_paid || 0))) });
                              setActionDialog({ type: 'markPaid', invoice: r });
                            } : undefined}
                            sx={{ bgcolor: c.bg, color: c.text, fontWeight: 600, cursor: canAct ? 'pointer' : 'default' }} />;
                        }
                        if (st === 'paid' || st === 'closed') {
                          return <Chip size="small" label="Paid"
                            onClick={() => setActionDialog({ type: 'paymentDetail', invoice: r })}
                            sx={{ bgcolor: statusColors.paid.bg, color: statusColors.paid.text, fontWeight: 600, cursor: 'pointer' }} />;
                        }
                        if (st === 'rejected') {
                          return <Chip size="small" label="Rejected" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600 }} />;
                        }
                        return <Chip size="small" label={st || '—'} />;
                      })()}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {invoiceUrl ? (
                        <IconButton size="small" onClick={() => setPreviewDialog({ open: true, url: invoiceUrl, title: 'Invoice ' + (r.invoice_number || '') })}>
                          <VisibilityIcon fontSize="small" sx={{ color: NAVY }} />
                        </IconButton>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {payUrl ? (
                        <IconButton size="small" onClick={() => setPreviewDialog({ open: true, url: payUrl, title: 'Payment — ' + (r.invoice_number || '') })}>
                          <ReceiptIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                        </IconButton>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* === Mark Paid dialog === */}
      {/* === Per-invoice Approve dialog === */}
      <Dialog open={actionDialog.type === 'approve'} onClose={() => !actionBusy && setActionDialog({ type: null, invoice: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Approve invoice</DialogTitle>
        <DialogContent>
          {actionDialog.invoice && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Approve <b>{actionDialog.invoice.invoice_number}</b> for{' '}
                <b>{moneyExact(actionDialog.invoice.amount)}</b>
                {actionDialog.invoice.vendor_name ? <> from <b>{actionDialog.invoice.vendor_name}</b></> : null}?
              </Typography>
              <Typography variant="caption" sx={{ color: '#777' }}>
                The vendor will be notified. The invoice can then be marked as paid.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ type: null, invoice: null })} disabled={actionBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleApprove} disabled={actionBusy}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0d1453' } }}>
            {actionBusy ? 'Approving…' : 'Approve invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* === Per-invoice Mark Paid dialog === */}
      <Dialog open={actionDialog.type === 'markPaid'} onClose={() => !actionBusy && setActionDialog({ type: null, invoice: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Record payment</DialogTitle>
        <DialogContent>
          {actionDialog.invoice && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Recording a payment for <b>{actionDialog.invoice.invoice_number}</b>
                {actionDialog.invoice.vendor_name ? <> to <b>{actionDialog.invoice.vendor_name}</b></> : null}.
                {' '}Invoice total <b>{moneyExact(actionDialog.invoice.amount)}</b>
                {Number(actionDialog.invoice.amount_paid || 0) > 0 ? <>, already paid <b>{moneyExact(actionDialog.invoice.amount_paid)}</b></> : null}.
              </Typography>
              <TextField label="Payment amount" size="small" fullWidth
                type="number" inputProps={{ step: '0.01', min: '0' }}
                value={singlePayForm.amount}
                onChange={e => setSinglePayForm({ ...singlePayForm, amount: e.target.value })}
                helperText={`Remaining balance: ${moneyExact(Math.max(0, Number(actionDialog.invoice.amount) - Number(actionDialog.invoice.amount_paid || 0)))}`} />
              <TextField label="Payment date" type="date" size="small" fullWidth
                value={singlePayForm.payment_date}
                onChange={e => setSinglePayForm({ ...singlePayForm, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <FormControl size="small" fullWidth>
                <InputLabel>Method</InputLabel>
                <Select label="Method" value={singlePayForm.payment_method}
                  onChange={e => setSinglePayForm({ ...singlePayForm, payment_method: e.target.value })}>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="ach">ACH / Bank transfer</MenuItem>
                  <MenuItem value="wire">Wire</MenuItem>
                  <MenuItem value="credit_card">Credit card</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Reference / Check #" size="small" fullWidth
                value={singlePayForm.payment_reference}
                onChange={e => setSinglePayForm({ ...singlePayForm, payment_reference: e.target.value })} />
              <Box>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
                  Payment proof (check image, receipt, etc.)
                </Typography>
                <FileUpload onFilesChange={(files) => setSinglePayForm(prev => ({ ...prev, attachments: files }))} />
                <Button size="small" startIcon={<CameraAltIcon />} onClick={() => setCheckCaptureOpen(true)}
                  sx={{ mt: 1, textTransform: 'none' }}>Take photo of check</Button>
                {singlePayForm.attachments && singlePayForm.attachments.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', mt: 0.5 }}>
                    {singlePayForm.attachments.length} attachment(s) ready
                  </Typography>
                )}
              </Box>
              <FormControlLabel
                control={<Checkbox size="small" checked={!!singlePayForm.cc_gc} onChange={e => setSinglePayForm(prev => ({ ...prev, cc_gc: e.target.checked }))} />}
                label={<Typography variant="caption" sx={{ color: '#555' }}>Copy the GC on the payment confirmation email</Typography>}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ type: null, invoice: null })} disabled={actionBusy}>Cancel</Button>
          <Button variant="contained" onClick={handleSingleMarkPaid} disabled={actionBusy}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
            {actionBusy ? 'Saving…' : 'Record payment'}
          </Button>
        </DialogActions>
      </Dialog>
      <CheckCapture open={checkCaptureOpen} onClose={() => setCheckCaptureOpen(false)}
        onCaptured={(att) => setSinglePayForm(prev => ({ ...prev, attachments: [...(prev.attachments || []), att] }))} />

      {/* === Payment Detail dialog (read-only) === */}
      <Dialog open={actionDialog.type === 'paymentDetail'} onClose={() => setActionDialog({ type: null, invoice: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Payment details</DialogTitle>
        <DialogContent>
          {actionDialog.invoice && (() => {
            const inv = actionDialog.invoice;
            const atts = Array.isArray(inv.attachments)
              ? inv.attachments
              : (() => { try { return JSON.parse(inv.attachments || '[]'); } catch { return []; } })();
            return (
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Box><Typography variant="caption" sx={{ color: '#777' }}>Invoice</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.invoice_number} — {moneyExact(inv.amount)}</Typography></Box>
                {payHistory.length > 0 && (() => {
                  const totalPaid = payHistory.reduce((s, p) => s + Number(p.amount || 0), 0);
                  const remaining = Math.max(0, Number(inv.amount) - totalPaid);
                  return (
                    <Box sx={{ p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#777', fontWeight: 700 }}>Payments ({payHistory.length})</Typography>
                        <Typography variant="caption" sx={{ color: remaining > 0 ? '#e65100' : '#2e7d32', fontWeight: 700 }}>
                          {moneyExact(totalPaid)} paid{remaining > 0 ? ` · ${moneyExact(remaining)} left` : ' · in full'}
                        </Typography>
                      </Box>
                      <Stack spacing={0.5}>
                        {payHistory.map(p => {
                          const pa = Array.isArray(p.attachments) ? p.attachments : (() => { try { return JSON.parse(p.attachments || '[]'); } catch { return []; } })();
                          return (
                            <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, borderBottom: '1px solid #e8eaf6', pb: 0.5 }}>
                              <span>{formatDate(p.payment_date || p.created_at)} · <b>{moneyExact(p.amount)}</b> · {p.payment_method || '—'}{p.payment_reference ? ` · ${p.payment_reference}` : ''}</span>
                              {pa[0]?.url ? <Button size="small" sx={{ minWidth: 0, textTransform: 'none' }} onClick={() => window.open(pa[0].url, '_blank')}>proof</Button> : null}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })()}
                <Box><Typography variant="caption" sx={{ color: '#777' }}>Payment date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(inv.paid_at)}</Typography></Box>
                <Box><Typography variant="caption" sx={{ color: '#777' }}>Method</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{inv.payment_method || '—'}</Typography></Box>
                <Box><Typography variant="caption" sx={{ color: '#777' }}>Reference</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.payment_reference || '—'}</Typography></Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#777' }}>Proof of payment</Typography>
                  {atts.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#999' }}>No attachment</Typography>
                  ) : (
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {atts.map((a, i) => (
                        <Button key={i} size="small" startIcon={<ReceiptIcon />}
                          onClick={() => window.open(a.url || a, '_blank')}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                          {a.name || a.filename || `Attachment ${i+1}`}
                        </Button>
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 0.5 }}>Replace check / update proof</Typography>
                  <FileUpload onFilesChange={(files) => setProofForm(f => ({ ...f, files }))} />
                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={<Checkbox size="small" checked={proofForm.notify} onChange={e => setProofForm(f => ({ ...f, notify: e.target.checked }))} />}
                    label={<Typography variant="caption" sx={{ color: '#555' }}>Notify recipient with corrected confirmation email</Typography>}
                  />
                  <Box sx={{ mt: 0.5 }}>
                    <Button size="small" variant="contained" disabled={proofForm.busy || proofForm.files.length === 0}
                      onClick={handleReplaceProof}
                      sx={{ bgcolor: '#2e7d32', textTransform: 'none' }}>
                      {proofForm.busy ? 'Saving...' : 'Save new proof'}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog({ type: null, invoice: null }); setProofForm({ files: [], notify: true, busy: false }); }}>Close</Button>
        </DialogActions>
      </Dialog>


      <Dialog open={markPaidOpen} onClose={() => setMarkPaidOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark {selectedIds.size} invoice(s) as paid</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Payment date" type="date" InputLabelProps={{ shrink: true }}
              value={markPaidForm.payment_date} onChange={e => setMarkPaidForm({ ...markPaidForm, payment_date: e.target.value })} />
            <FormControl>
              <InputLabel>Method</InputLabel>
              <Select value={markPaidForm.payment_method} label="Method"
                onChange={e => setMarkPaidForm({ ...markPaidForm, payment_method: e.target.value })}>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="ach">ACH</MenuItem>
                <MenuItem value="wire">Wire</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Reference (optional)" value={markPaidForm.payment_reference}
              onChange={e => setMarkPaidForm({ ...markPaidForm, payment_reference: e.target.value })} />
            <Typography variant="caption" sx={{ color: '#888' }}>
              Total: {moneyExact(selectedTotal)}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doMarkPaid} disabled={bulkBusy}>
            {bulkBusy ? 'Saving...' : `Mark ${selectedIds.size} Paid`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* === Document Preview Dialog === */}
      <Dialog open={previewDialog.open} onClose={() => setPreviewDialog({ open: false, url: null, title: '' })} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700, color: NAVY }}>
          {previewDialog.title}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => window.open(previewDialog.url, '_blank')} sx={{ textTransform: 'none' }}>
              Open in new tab
            </Button>
            <IconButton size="small" onClick={() => setPreviewDialog({ open: false, url: null, title: '' })}>
              <WarningIcon sx={{ display: 'none' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#999' }}>✕</Typography>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '75vh' }}>
          {previewDialog.url && (
            <iframe
              src={previewDialog.url}
              title={previewDialog.title}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={alert.show} autoHideDuration={4000} onClose={() => setAlert({ ...alert, show: false })}>
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })}>{alert.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  // Subs keep the old/legacy page until we redesign theirs
  if (user?.role === 'subcontractor') return <InvoicesLegacy />;
  return <Layout><InvoicesNew /></Layout>;
}
